/**
 * **************************************************************
 * Copyright (C) 2015-2020, 2023 VMware, Inc. All rights reserved.
 * **************************************************************
 *
 * @format
 */

/**
 * @fileoverview rtavsessioncontroller.js -- RtavSessionController
 * Class contains the logic for each rtav action, or how to controll classes to complete
 * a target action like "start streaming" or "enable audio".
 */

import RTAV from "./logservice";

/**
 * Class contains the main logic of rtav session, which will be used
 * by the RTAV session
 * @type {function}
 */
export default function RtavSessionController(sessionId, hardwareAccelerationOption) {
   let src,
      mediaEnc,
      sendingFunc,
      enableRTAVDTX,
      metaCacheA,
      metaCacheV,
      dataChannel = {
         audioEnable: false,
         videoEnable: false,
         // this flag is for fixing bug 1647044, where the device is missing but we should treat the enable as success
         videoMissing: false
      },
      hasResources = false,
      massageManager = null,
      /**
       * need to bind the sending function into a map with wmksId as key if we want to support seamless window,
       * that could be done using a new object named sessionManager, and I will not implement that for now.
       */
      initDoneCallback,
      audioDataCallback,
      videoDataCallback,
      //private functions
      resetProperties,
      initResources,
      initEncoder,
      releaseResources,
      closeResources,
      setResources;

   /**
    * reset for a new stream session
    * @param  {[type]} sendingFunction [description]
    * @return {[type]}                 [description]
    */
   resetProperties = function(sendingFunction) {
      sendingFunc = sendingFunction;
      metaCacheA = null;
      metaCacheV = null;
   };

   initEncoder = function(deviceParam) {
      let headerCallback = function(libHeader, isFackInit) {
         initDoneCallback(libHeader, deviceParam, isFackInit);
      };
      if (!mediaEnc.isInited()) {
         RTAV.logService.log("init encoders");
         mediaEnc.init(
            deviceParam.audio,
            deviceParam.video,
            deviceParam.codecPref,
            hardwareAccelerationOption,
            enableRTAVDTX,
            headerCallback,
            audioDataCallback,
            videoDataCallback
         );
      } else {
         RTAV.logService.log(
            "encoders has already been inited, so fetch the header again"
         );
         mediaEnc.fetchCachedHeader();
      }
   };

   setResources = function(resources, onDone) {
      if (!resources) {
         RTAV.logService.log(
            "find no avaliable audio-in or video-in devices, so RTAV function is not usable"
         );
         onDone(false);
      } else {
         src = resources.src;
         mediaEnc = resources.mediaEnc;
         if (massageManager) {
            mediaEnc.setMessageManager(massageManager);
         }
         RTAV.logService.debug(
            "session " +
               sessionId +
               " successfully get RTAV devices control rights"
         );
         onDone(true);
      }
   };

   initResources = function(onDone) {
      let stolenSessionId, confirmDeviceSteal, cancelDeviceSteal;

      if (!RTAV.resourceManager.hasOccupiedResources(sessionId)) {
         if (RTAV.resourceManager.hasUsableResources()) {
            RTAV.resourceManager.occupyResources(sessionId, function(
               resources
            ) {
               setResources(resources, onDone);
            });
         } else {
            if (RTAV.dialogService.showDialog) {
               confirmDeviceSteal = function() {
                  RTAV.resourceManager.stealSession(sessionId, function(
                     resources
                  ) {
                     RTAV.logService.debug(
                        "session " +
                           sessionId +
                           " trying to steal the devices from session " +
                           stolenSessionId
                     );
                     setResources(resources, onDone);
                  });
               };

               cancelDeviceSteal = function() {
                  RTAV.logService.log(
                     "session " +
                        sessionId +
                        " failed to occupy devices, so RTAV function is not usable"
                  );
                  onDone(false);
               };

               stolenSessionId = RTAV.resourceManager.getWorkingSessionId();
               // skip confirmation if devices are not used
               if (!RTAV.wmksAdapter.isUsingDevices(stolenSessionId)) {
                  confirmDeviceSteal();
                  return;
               }
               RTAV.dialogService.showDialog(
                  {
                     stolenSessionId: stolenSessionId
                  },
                  confirmDeviceSteal,
                  cancelDeviceSteal
               );
            } else {
               RTAV.logService.log(
                  "session " +
                     sessionId +
                     " failed to occupy devices, so RTAV function is not usable"
               );
               onDone(false);
            }
         }
      } else {
         onDone(true);
      }
   };

   /**
    * release all local resources
    */
   releaseResources = function() {
      // clear local refer
      src = null;
      mediaEnc = null;
      // release
      if (RTAV.resourceManager.hasOccupiedResources(sessionId)) {
         RTAV.resourceManager.releaseResources(sessionId);
      }
      // reset flag
      hasResources = false;
      return true;
   };

   /**
    * close the hold resources, then the resources are not usable anymore
    */
   closeResources = function() {
      // release
      if (RTAV.resourceManager.hasOccupiedResources(sessionId) && !!src) {
         src.close();
      }
   };

   return {
      /**
       * set all the callbacks
       */
      initialize: function() {
         // inited header callback
         initDoneCallback = function(libHeader, deviceParam, isFackInit) {
            RTAV.logService.trace("header generated: ");
            if (deviceParam.codecPref !== RTAV.constants.codecTypes['CodecVmwH264Opus']) {
               if (!libHeader || !libHeader.buffer) {
                  RTAV.logService.error(
                     "fail to get header, RTAV will stop working"
                  );
                  return;
               }
               RTAV.logService.trace(new Uint8Array(libHeader.buffer));
               sendingFunc({
                  streamData: libHeader,
                  dataCount: 1,
                  timeStamp: 0
               });
            }
            if (!isFackInit && src) {
               this.deviceParam = deviceParam;
               RTAV.logService.debug("start streaming with encoder inited");
               src.startStream(
                  deviceParam,
                  function(data) {
                     mediaEnc.encodeAudio(data);
                  },
                  function(data) {
                     mediaEnc.encodeVideo(data);
                  }
               );
            }
         }.bind(this);
         this.initDoneCallback = initDoneCallback;
         // audio encoded callback, no others needed to be dealt so far
         audioDataCallback = function(result, timeStamp) {
            let metaCount = 1;
            if (!dataChannel.audioEnable) {
               return;
            }
            if (result) {
               if (metaCacheA !== null) {
                  sendingFunc({
                     streamData: result,
                     dataCount: metaCacheA + metaCount,
                     timeStamp: timeStamp
                  });
                  metaCacheA = null;
               } else {
                  sendingFunc({
                     streamData: result,
                     dataCount: metaCount,
                     timeStamp: timeStamp
                  });
               }
            } else {
               if (metaCacheA !== null) {
                  metaCacheA += metaCount;
               } else {
                  metaCacheA = metaCount;
               }
            }
         };

         // video encoded callback
         videoDataCallback = function(result, timeStamp, others) {
            let metaCount = 1,
               onDoneCallback,
               callbackParam;

            if (!dataChannel.videoEnable) {
               return;
            }
            if (result) {
               if (metaCacheV !== null) {
                  sendingFunc({
                     streamData: result,
                     dataCount: metaCacheV + metaCount,
                     timeStamp: timeStamp
                  });
                  metaCacheV = null;
               } else {
                  sendingFunc({
                     streamData: result,
                     dataCount: metaCount,
                     timeStamp: timeStamp
                  });
               }
            } else {
               if (metaCacheV !== null) {
                  metaCacheV += metaCount;
               } else {
                  metaCacheV = metaCount;
               }
            }
            if (others) {
               onDoneCallback = others.callback;
               callbackParam = others.callbackParam;
               if (typeof onDoneCallback === "function") {
                  onDoneCallback(callbackParam);
               }
            }
         };
      },

      /**
       * The API for start a rtav streaming
       * @param  {object} encoderParam The params that will be used to config the source and encoder which generating the stream data.
       * @param  {function} sendingFunction The callback that will be used to send the stream data.
       * @return {boolean} This returns whether we can start a new rtav stream task.
       */
      startMediaIn: function(encoderParam, sendingFunction) {
         resetProperties(sendingFunction);
         if (!hasResources) {
            RTAV.logService.debug("no resource to start");
            return false;
         }
         RTAV.logService.debug("start mediaIn");
         initEncoder(encoderParam);
         return true;
      },

      /**
       * The API for stop a rtav streaming
       */
      stopMediaIn: function() {
         if (!hasResources) {
            return;
         }
         RTAV.logService.debug("stop mediaIn");
         if (dataChannel.audioEnable) {
            this.disableAudioIn(() => {
               RTAV.logService.debug("audio stopped");
            });
         }
         if (dataChannel.videoEnable) {
            this.disableVideoIn(() => {
               RTAV.logService.debug("video stopped");
            });
         }
      },

      setAudioDTXMode : function(isAudioDTXEnabled) {
         enableRTAVDTX = isAudioDTXEnabled;
      },

      /**
       * The API for enable audio in
       * we need to wait the sources inited which is a async process, then see whether this task can be processed successfullly
       * @param {function} callback will return whether enable success
       * @param {object} deviceMessageController This param is used to emit premission dialog related events
       */
      enableAudioIn: function(callback, deviceMessageController) {
         let whenHasResources = function(canProcess) {
            if (!canProcess) {
               RTAV.logService.debug(
                  "enable audio can't be done since current session has no resources"
               );
               callback(false);
               return;
            }
            if (!src) {
               RTAV.logService.error("no src, inner error found");
               callback(false);
               return;
            }
            RTAV.logService.debug("enable audio for current session");
            src.enable("audio", deviceMessageController).then(
               (enableSuccess) => {
                  RTAV.logService.debug(
                     "audio enable result: " + enableSuccess
                  );
                  dataChannel.audioEnable = enableSuccess;
                  if (enableSuccess) {
                     mediaEnc.enable("audio");
                  }
                  callback(enableSuccess);
                  RTAV.resourceManager.emitDeviceStatusChanged();
               }
            );
         };
         this.processWhenHaveResources(whenHasResources);
      },

      /**
       * The API for disable audio in
       * we need to wait the sources inited which is a async process, then see whether this task can be processed successfullly
       * @return {boolean} This returns whether the disable action success, but treat to disable a released encoder as success
       */
      disableAudioIn: function(callback) {
         RTAV.logService.debug("disabling audio for current session");
         if (!hasResources) {
            RTAV.logService.log(
               "disable success for the encoder and src has already been released"
            );
            callback(true);
         }
         if (!src) {
            RTAV.logService.error("no src, inner error found");
            callback(true);
         }
         if (!dataChannel.audioEnable) {
            RTAV.logService.log(
               "audio has already being disabled, but still disable again"
            );
         }
         dataChannel.audioEnable = false;

         RTAV.logService.debug("disabling audio");
         Promise.all([src.disable("audio"), mediaEnc.disable("audio")]).then(
            (results) => {
               let success = results[0] && results[1];
               if (!success) {
                  RTAV.logService.error(
                     "disable audio failed, device disable: " +
                        results[0] +
                        "encoder disable: " +
                        results[1]
                  );
               } else {
                  RTAV.logService.debug(
                     "disable audio device and encoder done"
                  );
               }
               callback(success);
               RTAV.resourceManager.emitDeviceStatusChanged();
            }
         );
      },

      /**
       * The API for enable video in
       * we need to wait the sources inited which is a async process, then see whether this task can be processed successfullly
       * @param {function} callback will return whether enable success
       * @param {object} deviceMessageController This param is used to emit premission dialog related events
       */
      enableVideoIn: function(callback, deviceMessageController) {
         let whenHasResources = function(canProcess) {
            if (!canProcess) {
               RTAV.logService.debug(
                  "enable video can't be done since current session has no resources"
               );
               callback(false);
               return;
            }
            if (!src) {
               RTAV.logService.error("no src, inner error found");
               callback(false);
               return;
            }
            RTAV.logService.debug("enable video for current session");
            src.enable("video", deviceMessageController).then((success) => {
               // check bug 1647044 for detail
               let enableSuccess = true;
               let missingDevice = !success;
               RTAV.logService.debug(
                  "video enable result: " +
                     enableSuccess +
                     " , with device missing status: " +
                     missingDevice
               );
               dataChannel.videoEnable = enableSuccess;
               dataChannel.videoMissing = !!missingDevice;
               if (enableSuccess) {
                  mediaEnc.enable("video");
               }
               callback(enableSuccess);
               RTAV.resourceManager.emitDeviceStatusChanged();
            });
         };
         this.processWhenHaveResources(whenHasResources);
      },

      /**
       * The API for disable video in
       * we need to wait the sources inited which is a async process, then see whether this task can be processed successfullly
       * @return {boolean} This returns whether the disable action success, but treat to disable a released encoder as success
       */
      disableVideoIn: function(callback) {
         if (!hasResources) {
            RTAV.logService.log(
               "disable success for the encoder and src has already been released"
            );
            callback(true);
         }
         if (!dataChannel.videoEnable) {
            RTAV.logService.log(
               "video has already being disabled, so no need to disable again"
            );
            callback(true);
         }
         if (!src) {
            RTAV.logService.error("no src, inner error found");
            callback(true);
         }

         RTAV.logService.debug("disabling video for current session");
         Promise.all([src.disable("video"), mediaEnc.disable("video")]).then(
            (results) => {
               let success = results[0] && results[1];
               if (!success) {
                  RTAV.logService.error(
                     "disable video failed, device disable: " +
                        results[0] +
                        "encoder disable: " +
                        results[1]
                  );
               } else {
                  RTAV.logService.debug(
                     "disable video device and encoder done"
                  );
               }
               callback(success);
               RTAV.resourceManager.emitDeviceStatusChanged();
            }
         );
         dataChannel.videoEnable = false;
         dataChannel.videoMissing = false;
      },

      /**
       * The API for user level of stop of RTAV
       * should be called when the rtav is turned off by user for a specified session
       * @return {boolean} This returns whether the userStop action success
       */
      userStop: function() {
         RTAV.logService.log("user stop WebCam/Mic redirection");
         this.stopMediaIn();
         releaseResources();
      },

      /**
       * The API for session level of stop of RTAV
       * should be called only when the hosted session is disconnected
       * @return {boolean} This returns whether the stop action success
       */
      stop: function() {
         RTAV.logService.log("stop WebCam/Mic redirection");
         this.stopMediaIn();
         closeResources();
         releaseResources();
      },

      /**
       * @return {Boolean} This returns whether the controller is send stream in any kind
       */
      isSendingStream: function(type) {
         if (!hasResources) {
            return false;
         }
         if (!src) {
            return false;
         }
         if (type === "video") {
            return (
               (dataChannel.videoEnable &&
                  !dataChannel.videoMissing &&
                  src.isActive(type)) ||
               this.deviceMessageManager.isPermissionPending(type)
            );
         } else if (type === "audio") {
            return (
               (dataChannel.audioEnable && src.isActive(type)) ||
               this.deviceMessageManager.isPermissionPending(type)
            );
         }
         return (
            (dataChannel.videoEnable &&
               !dataChannel.videoMissing &&
               src.isActive("video")) ||
            (dataChannel.audioEnable && src.isActive("audio")) ||
            this.deviceMessageManager.isPermissionPending()
         );
      },

      /**
       * Try to get the resource, and when do, call processFunc, and call callback(true)
       * if not, call callback(false)
       * @param {function} processFunc The function that will be called when the resources are ready
       */
      processWhenHaveResources: function(processFunc) {
         if (!hasResources) {
            initResources(
               function(success) {
                  if (success) {
                     RTAV.logService.trace("get resources for operation");
                     hasResources = success;
                     processFunc(true);
                  } else {
                     RTAV.logService.trace("don't get resources for operation");
                     processFunc(false);
                  }
               }.bind(this)
            );
         } else {
            processFunc(true);
         }
      },

      /**
       * set messageManager into encoder so that we can check the agent-client
       * mis-match status to avoid bug 1750132.
       * Never clear massageManager since it's const and persistent
       * @param {object} manager A DeviceMessageManager instance
       */
      setMessageManager: function(manager) {
         massageManager = manager;
         if (mediaEnc) {
            mediaEnc.setMessageManager(manager);
         }
      },

      /**
       * keep the encoder release to avoid accumulated error
       */
      onPermissionHandled: function() {
         if (!mediaEnc || !mediaEnc.isInited()) {
            return;
         }
         if (mediaEnc.notInUse()) {
            RTAV.logService.warn(
               "no encoder is enabled after device permission handled"
            );
            mediaEnc.clear();
         }
      },

      initDoneCallback
   };
}
